// src/pages/admin/companySummary/helpers.js — pure formatting/calculation helpers and export
// column config used by CompanySummaryTab. No React state; every function is a function of
// its arguments only.

export const startOfDayMs = (dStr) => new Date(`${dStr}T00:00:00`).getTime();
export const nextDayMs = (dStr) =>
  new Date(`${dStr}T00:00:00`).getTime() + 24 * 60 * 60 * 1000;
export const FUTILE_CHARGE_AMOUNT = 180;

export const ADVANCED_EXPORT_COLUMNS = [
  "driver",
  "customer",
  "date",
  "address",
  "description",
  "site",
  "service",
  "qtyBinsCollected",
  "unitAmount",
  "lineTotal",
  "silicaWeightKg",
  "silicaBags",
  "silicaAmount",
  "chairsQty",
  "chairsAmount",
  "palletsQty",
  "palletsAmount",
  "wasteChargesTotal",
  "futileCharge",
  "futileNote",
  "driverNotes",
  "driverEmail",
  "poNumber",
  "totalAmount",
];

export const ADVANCED_EXPORT_OPTIONS = [
  { key: "driver", label: "Driver" },
  { key: "driverEmail", label: "Driver Email" },
  { key: "customer", label: "Customer" },
  { key: "date", label: "Date" },
  { key: "address", label: "Address" },
  { key: "description", label: "Description" },
  { key: "site", label: "Site" },
  { key: "service", label: "Service" },
  { key: "qtyBinsCollected", label: "Qty (bins)" },
  { key: "unitAmount", label: "Unit Amount" },
  { key: "lineTotal", label: "Line Total" },
  { key: "silicaWeightKg", label: "Silica Weight (kg)" },
  { key: "silicaBags", label: "Silica Bags" },
  { key: "silicaAmount", label: "Silica Amount" },
  { key: "chairsQty", label: "Chairs Qty" },
  { key: "chairsAmount", label: "Chairs Amount" },
  { key: "palletsQty", label: "Pallets Qty" },
  { key: "palletsAmount", label: "Pallets Amount" },
  { key: "wasteChargesTotal", label: "Waste Charges Total" },
  { key: "futileCharge", label: "Futile Charge" },
  { key: "futileNote", label: "Futile Note" },
  { key: "driverNotes", label: "Driver Notes" },
  { key: "vehicleReg", label: "Vehicle Reg" },
  { key: "poNumber", label: "PO Number" },
  { key: "company", label: "Company" },
  { key: "totalAmount", label: "Total Amount" },
];

export const addrToText = (addr) => {
  if (!addr) return "";
  if (typeof addr === "string") return addr;
  if (typeof addr === "object") {
    const {
      line1,
      line2,
      suburb,
      city,
      town,
      state,
      stateCode,
      postcode,
      postCode,
      postalCode,
      country,
      address,
      formatted,
      label,
      name,
    } = addr;
    const parts = [
      formatted || address || label || name || "",
      line1,
      line2,
      suburb || city || town,
      state || stateCode,
      postcode || postCode || postalCode,
      country,
    ].filter(Boolean);
    const seen = new Set();
    return parts
      .filter((p) => (seen.has(p) ? false : (seen.add(p), true)))
      .join(", ");
  }
  return String(addr);
};

export function tsToMs(t) {
  if (!t) return 0;
  if (typeof t === "number") return t;
  if (t?.seconds) return t.seconds * 1000;
  const n = +new Date(t);
  return Number.isFinite(n) ? n : 0;
}
export const inRange = (ms, a, b) => ms >= a && ms < b;

export const fmtAU = (t) => {
  const ms = tsToMs(t);
  if (!ms) return "";
  return new Date(ms).toLocaleString("en-AU", {
    timeZone: "Australia/Melbourne",
  });
};

export const fmtDateAU = (d) => {
  if (!d) return "";
  const ms = tsToMs(d) || tsToMs(`${d}T00:00:00`);
  if (!ms) return String(d || "");
  return new Date(ms).toLocaleDateString("en-AU", {
    timeZone: "Australia/Melbourne",
  });
};

export const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;

export const normalizeText = (v) =>
  String(v || "")
    .trim()
    .toLowerCase();

export const slugify = (v) =>
  normalizeText(v)
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

export const makeSiteSelectionKey = (customerId, site, fallback = "") => {
  const siteId = String(site?.id || site?.siteId || "").trim();
  const label = normalizeText(site?.label || site?.siteName || site?.name || fallback);
  return `${String(customerId || "").trim()}__${siteId || label}`;
};

export const toNumber = (v) => {
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  if (typeof v === "string") {
    const cleaned = v.replace(/[^\d.-]/g, "");
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  if (v?.seconds) return Number(v.seconds) || 0;
  return 0;
};

export const hasValue = (v) =>
  !(v === undefined || v === null || (typeof v === "string" && v.trim() === ""));

export const firstDefined = (...vals) => vals.find((v) => hasValue(v));

export const getQty = (item) =>
  toNumber(
    firstDefined(
      item?.qty,
      item?.quantity,
      item?.units,
      item?.count,
      item?.binQty,
      item?.serviceBinQty,
      item?.billableBinQty
    )
  );

export const fmtCurrency = (value) => {
  if (!hasValue(value) && value !== 0) return "";
  const n = toNumber(value);
  if (!Number.isFinite(n)) return "";
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
  }).format(n);
};

export const parseQtyFromText = (text = "") => {
  const str = String(text || "");
  const m = str.match(/(\d+(?:\.\d+)?)\s*x\s*bins?/i);
  return m ? toNumber(m[1]) : 0;
};

export const canonicalServiceLabel = (value, fallback = "") => {
  const raw = String(value || fallback || "").trim();
  const key = slugify(raw);
  if (!key) return raw;
  if (key.includes("deliver")) return "DELIVER";
  if (
    key.includes("pickup") ||
    key.includes("pick-up") ||
    (key.includes("pick") && key.includes("up"))
  ) return "Pick Up";
  if (key.includes("empty") || key.includes("waste")) return "EMPTY";
  return raw;
};

export const isSilicaMaterialName = (name) =>
  normalizeText(name).includes("silica");

export const isChairChargeName = (name) =>
  normalizeText(name).includes("chair");

export const isPalletChargeName = (name) =>
  normalizeText(name).includes("pallet");

export const isTruthyPrice = (v) => {
  if (v === undefined || v === null || v === "") return false;
  const n = toNumber(v);
  return Number.isFinite(n);
};

export const componentSortMs = (component, fallback = 0) => {
  return (
    tsToMs(
      firstDefined(
        component?.completedAt,
        component?.serviceAt,
        component?.eventAt,
        component?.updatedAt,
        component?.createdAt,
        component?.timestamp,
        component?.time
      )
    ) || fallback
  );
};

export const sumQtyArray = (arr = []) =>
  (Array.isArray(arr) ? arr : []).reduce((sum, entry) => sum + getQty(entry), 0);

export const summarizeWasteCharges = (job = {}, jobLoads = []) => {
  const rowsByKey = {};
  const addRow = (row, idx) => {
    if (!row) return;
    const key = `${row.createdAt || row.id || idx}_${row.binId || ""}`;
    rowsByKey[key] = row;
  };

  (Array.isArray(job.emptyLoads) ? job.emptyLoads : []).forEach((row, idx) =>
    addRow(row, idx)
  );
  (Array.isArray(jobLoads) ? jobLoads : []).forEach((row, idx) =>
    addRow(row, idx)
  );

  return Object.values(rowsByKey).reduce(
    (acc, row) => {
      const materials = Array.isArray(row.materials) ? row.materials : [];
      const materialText = materials.map((m) => m?.name || "").join(" ");
      const chargeType = row.chargeType || materialText || row.wasteType || "";
      const chargeQuantity = toNumber(row.chargeQuantity);
      const amount = toNumber(row.amount);

      if (isSilicaMaterialName(chargeType) || toNumber(row.silicaWeightKg) > 0) {
        const weight = toNumber(row.silicaWeightKg);
        acc.silicaWeightKg += weight;
        acc.silicaBags += toNumber(row.bagCount);
        acc.silicaAmount += amount || weight;
      }

      if (isChairChargeName(chargeType)) {
        acc.chairsQty += chargeQuantity;
        acc.chairsAmount += amount || chargeQuantity * 10;
      }

      if (isPalletChargeName(chargeType)) {
        acc.palletsQty += chargeQuantity;
        acc.palletsAmount += amount || chargeQuantity * 10;
      }

      return acc;
    },
    {
      silicaWeightKg: 0,
      silicaBags: 0,
      silicaAmount: 0,
      chairsQty: 0,
      chairsAmount: 0,
      palletsQty: 0,
      palletsAmount: 0,
    }
  );
};

export const getDateRangeArray = (from, to) => {
  const dates = [];
  const start = new Date(`${from}T00:00:00`);
  const end = new Date(`${to}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return dates;

  const cur = new Date(start);
  while (cur <= end) {
    dates.push(
      cur.toLocaleDateString("en-CA", { timeZone: "Australia/Melbourne" })
    );
    cur.setDate(cur.getDate() + 1);
  }
  return dates;
};
